import { Application, ApplicationPartnerTypeEnum, ApplicationStatusEnum } from '@tran-monorepo/interfaces/applications';
import { File as FileData } from '@tran-monorepo/interfaces/files';
import { isApplicationProgressValueComplete } from './application-progress-validators.utils';

export interface ApplicationProgressField {
    /** Key matching the form control name in ApplicationFormModel */
    readonly formKey: string;
    /**
     * Checks whether this field is complete given a persisted Application object.
     * For the `documents` field this always returns false — pass `files` to
     * `calculateApplicationProgress` for the real check.
     */
    readonly isCompleteFromApplication: (
        application: Application,
        files?: Pick<FileData, 'name' | 'status'>[]
    ) => boolean;
}

/**
 * Single source of truth for which fields count towards application progress.
 *
 * Each entry maps a form control key to an equivalent completeness check
 * that works directly on a persisted Application object (used by the tile view).
 *
 * The `documents` field requires a separate files list — pass it as the second
 * argument to `calculateApplicationProgress`.
 */
export const APPLICATION_PROGRESS_FIELDS: readonly ApplicationProgressField[] = [
    {
        formKey: 'name',
        isCompleteFromApplication: app => isApplicationProgressValueComplete('name', app.name)
    },
    {
        formKey: 'applicants',
        isCompleteFromApplication: app => isApplicationProgressValueComplete('applicants', app.applicants ?? [])
    },
    {
        formKey: 'locations',
        isCompleteFromApplication: app => isApplicationProgressValueComplete('locations', app.locations ?? [])
    },
    {
        formKey: 'affectedOrgUnits',
        isCompleteFromApplication: app =>
            isApplicationProgressValueComplete('affectedOrgUnits', app.affectedOrgUnits ?? [])
    },
    {
        formKey: 'learningGrowthPartner',
        isCompleteFromApplication: app =>
            isApplicationProgressValueComplete(
                'learningGrowthPartner',
                (app.partners ?? []).find(p => p.partnerType === ApplicationPartnerTypeEnum.LearningGrowthPartner) ??
                    null
            )
    },
    {
        formKey: 'betriebsrat',
        isCompleteFromApplication: app =>
            isApplicationProgressValueComplete(
                'betriebsrat',
                (app.partners ?? []).find(p => p.partnerType === ApplicationPartnerTypeEnum.Betriebsrat) ?? null
            )
    },
    {
        formKey: 'technicalResponsible',
        isCompleteFromApplication: app =>
            isApplicationProgressValueComplete('technicalResponsible', app.technicalResponsible ?? null)
    },
    {
        formKey: 'businessResponsible',
        isCompleteFromApplication: app =>
            isApplicationProgressValueComplete('businessResponsible', app.businessResponsible ?? null)
    },
    {
        formKey: 'commercialResponsible',
        isCompleteFromApplication: app =>
            isApplicationProgressValueComplete('commercialResponsible', app.commercialResponsible ?? null)
    },
    {
        formKey: 'affectedEmployees',
        isCompleteFromApplication: app =>
            isApplicationProgressValueComplete('affectedEmployees', app.affectedEmployees ?? null)
    },
    {
        formKey: 'budgets',
        isCompleteFromApplication: app => isApplicationProgressValueComplete('budgets', app.budgets ?? [])
    },
    {
        formKey: 'employeeLearningHours',
        isCompleteFromApplication: app =>
            isApplicationProgressValueComplete('employeeLearningHours', app.employeeLearningHours ?? null)
    },
    {
        formKey: 'documents',
        isCompleteFromApplication: (_app, files) => isApplicationProgressValueComplete('documents', files ?? [])
    },
    {
        formKey: 'subjectAreaSelection',
        isCompleteFromApplication: app =>
            isApplicationProgressValueComplete('subjectAreaSelection', {
                subjectAreas: app.subjectAreas ?? [],
                additionalSubjectAreas: app.additionalSubjectAreas ?? []
            })
    },
    {
        formKey: 'contribution',
        isCompleteFromApplication: app => isApplicationProgressValueComplete('contribution', app.contribution ?? null)
    },
    {
        formKey: 'involvement',
        isCompleteFromApplication: app => isApplicationProgressValueComplete('involvement', app.involvement ?? null)
    },
    {
        formKey: 'transferAndScalability',
        isCompleteFromApplication: app =>
            isApplicationProgressValueComplete('transferAndScalability', app.transferAndScalability ?? null)
    }
] as const;

/**
 * Ordered list of form control keys that map to APPLICATION_PROGRESS_FIELDS.
 * Use this to derive the progressControls array from a typed FormGroup.
 */
export const APPLICATION_PROGRESS_FORM_KEYS: readonly string[] = APPLICATION_PROGRESS_FIELDS.map(f => f.formKey);

/**
 * Calculates the completion percentage (0–100) of an application
 * based on APPLICATION_PROGRESS_FIELDS.
 *
 * Non-DRAFT applications are considered fully complete and always return 100.
 *
 * @param files Optional list of uploaded files for the documents completeness check.
 *              When omitted, the documents field counts as incomplete.
 */
export function calculateApplicationProgress(
    application: Application,
    files?: Pick<FileData, 'name' | 'status'>[]
): number {
    if (application.status !== ApplicationStatusEnum.Draft) {
        return 100;
    }
    const total = APPLICATION_PROGRESS_FIELDS.length;
    const done = APPLICATION_PROGRESS_FIELDS.filter(field =>
        field.isCompleteFromApplication(application, files)
    ).length;
    return total === 0 ? 0 : Math.round((done / total) * 100);
}
